<template>
	<v-menu
		top
		class="pa-0"
		color="#00000000"
		v-model="show"
		transition="scroll-y-transition"
		:close-on-content-click="false"
		offset-y
		nudge-top="10"
		nudge-left="271"
		origin="bottom center"
		overflow-y="hidden">
		<template v-slot:activator="{ on }">
			<v-progress-circular v-on="on" :rotate="90" size="33" width="4" :value="OverAll" :color="getOverallStatusColor()">
				<v-icon v-if="access === 'download'" color="primary" size="25">mdi-download-circle</v-icon>
				<v-icon v-else-if="access === 'upload'" color="primary" size="25">mdi-upload-circle</v-icon>
				<v-icon v-else color="primary" size="25">mdi-swap-vertical-circle</v-icon>
			</v-progress-circular>
		</template>

		<v-card class="rounded-lg" color="#f9f8fb" width="600">
			<v-card-title class="font-weight-bold responsive-font-md pa-2">
				{{ $t('jobs-header') }}
				<span class="font-weight-light font-size13 ml-2">({{ OverAll }}%)</span>
				<v-spacer> </v-spacer>

				<v-icon color="primary" @click="show = false"> mdi-close </v-icon>
			</v-card-title>
			<v-card-text class="pa-2" style="overflow-y: auto; max-height: 20vh">
				<v-data-table :items="jobs" hide-default-footer fixed-header no-data-text="No jobs found" dense sort-desc>
					<template v-slot:item="{ item }">
						<tr style="background: #f9f8fb; cursor: pointer">
							<td class="text-left px-2 truncate-text" style="max-width: 290px">
								<v-icon
									v-if="access === 'both'"
									:size="$vuetify.breakpoint.mdAndDown ? 19 : 21"
									color="black"
									class="mr-1">
									{{ 'mdi-' + item.direction }}
								</v-icon>
								<v-icon :size="$vuetify.breakpoint.mdAndDown ? 19 : 21" color="black" class="mr-1">
									{{ getFileIcon(item.fileName) }}
								</v-icon>
								{{ item.fileName }}
							</td>

							<td class="text-left px-1" style="white-space: nowrap; max-width: 70px">
								({{ getReadableTotalSize(item.totalSize) }})
							</td>

							<td class="text-left px-1" style="min-width: 145px; width: 180px">
								<v-progress-linear
									:color="item.status !== 'failed' ? 'green' : 'red'"
									rounded
									striped
									:height="14"
									:value="item.percentage"
									class="font-weight-bold font-size13">
									{{ item.percentage }}%
								</v-progress-linear>
							</td>
							<td class="text-right px-1" style="width: 56px; min-width: 56px">
								<v-icon
									:size="$vuetify.breakpoint.mdAndDown ? 19 : 21"
									:color="item.status !== 'failed' ? 'green' : 'red'">
									{{ getStatusIcon(item.status) }}
								</v-icon>
								<v-icon
									:size="$vuetify.breakpoint.mdAndDown ? 19 : 21"
									color="primary"
									:disabled="item.status === 'failed' || item.percentage < 100"
									@click="$emit('delete-job', item.id)">
									mdi-close-thick
								</v-icon>
							</td>
						</tr>
					</template>
				</v-data-table>
			</v-card-text>
		</v-card>
	</v-menu>
</template>

<script>
export default {
	name: 'TransmissionJobsDialog',
	props: {
		jobs: {
			type: Array,
			required: true,
			default: () => [],
		},
		access: {
			type: String,
			default: 'both', // 'upload', 'download', 'both'
		},
	},
	data () {
		return {
			show: false,
		};
	},

	computed: {
		jobsCount () {
			return this.jobs.length;
		},

		OverAll () {
			// Only consider jobs that are not completed or failed
			const activeJobs = this.jobs.filter((job) => job.percentage < 100 && job.status !== 'failed');
			if (activeJobs.length === 0 && this.jobs.length > 0) return 100;
			if (activeJobs.length === 0) return 0;
			const totalProgress = activeJobs.reduce((sum, job) => sum + job.percentage, 0);
			return Math.round(totalProgress / activeJobs.length);
		},
	},

	methods: {
		getReadableTotalSize (bytes) {
			if (typeof bytes !== 'number' || bytes < 0) {
				return 'Invalid size';
			}

			const units = ['B', 'KB', 'MB', 'GB'];
			let unitIndex = 0;
			let size = bytes;

			while (size >= 1024 && unitIndex < units.length - 1) {
				size /= 1024;
				unitIndex++;
			}
			return `${size.toFixed(2)} ${units[unitIndex]}`;
		},

		getStatusIcon (status) {
			const statusIcons = {
				uploading: 'mdi-cloud-upload',
				downloading: 'mdi-cloud-download',
				completed: 'mdi-check-circle',
				failed: 'mdi-alert-circle',
			};
			return statusIcons[status];
		},

		getOverallStatusColor () {
			let doneCount = 0;
			let failedCount = 0;
			let uploadingCount = 0;
			// const downloadingCount = 0;

			for (const job of this.jobs) {
				if (job.status === 'completed') {
					doneCount++;
				} else if (job.status === 'failed') {
					failedCount++;
				} else if (job.status === 'uploading') {
					uploadingCount++;
				}
			}

			if (uploadingCount > 0 && failedCount > 0) {
				return 'orange';
			} else if (failedCount > 0 && doneCount > 0) {
				return 'orange';
			} else if (failedCount > 0 && doneCount === 0) {
				return 'red';
			} else {
				return 'green';
			}
		},

		getFileIcon (fileName) {
			const nameArray = fileName.toString().split('.');
			const fileType = nameArray[nameArray.length - 1];

			const fileTypeMap = {
				pdf: 'mdi-file-powerpoint',
				jpeg: 'mdi-file-image',
				jpg: 'mdi-file-image',
				png: 'mdi-file-image',
				txt: 'mdi-file-document',
				doc: 'mdi-file-document',
				docx: 'mdi-file-document',
				xls: 'mdi-file-table',
				xlsx: 'mdi-file-table',
				ppt: 'mdi-file-chart',
				pptx: 'mdi-file-chart',
				csv: 'mdi-file-delimited',
				zip: 'mdi-zip-box',
				'7z': 'mdi-zip-box',
				mp4: 'mdi-file-video',
				mkv: 'mdi-file-video',
				mp3: 'mdi-file-music',
				avi: 'mdi-file-video',
				ogg: 'mdi-file-music',
				rar: 'mdi-zip-box',
			};
			return fileTypeMap[fileType] || 'mdi-file';
		},
	},

	watch: {
		jobsCount () {
			this.show = true; // Show the dialog when there are jobs
		},
	},
};
</script>

<style scoped>
.v-data-table__empty-wrapper {
	background-color: #f9f8fb !important;
}
</style>
